import React, { useEffect } from 'react';
import {
  useTable,
  usePagination,
  TableInstance,
  Row,
  useFilters,
  useSortBy,
} from 'react-table';
import { useHistory } from 'react-router-dom';
import { Pagination } from 'components/Pagination';
import { Table as TableStyled } from './TableStyles';
import { Column } from 'types/Table';
import { Skeleton } from 'components/Skeleton';

type Props = {
  data: any[];
  columns: Column[];
  pagination?: boolean;
  autoReset?: boolean;
  noResultsMessage?: string;
  selector?: string;
  useQueryParams?: boolean;
  additionalData?: Array<{ key: string; value: any }>;
  isLoading?: boolean;
  rowProps?: (row: any) => any;
};

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}: any) {
  const count = preFilteredRows.length;

  return (
    <input
      style={{ width: '100%' }}
      value={filterValue || ''}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

export const Table: React.FC<Props> = ({
  data,
  columns,
  pagination,
  autoReset,
  noResultsMessage = '',
  selector,
  useQueryParams,
  additionalData,
  isLoading = false,
  rowProps,
}) => {
  const history = useHistory();
  const pageIndex = useQueryParams
    ? new URLSearchParams(history.location.search).get('page')
    : 1;

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    pageOptions,
    gotoPage,
    setPageSize,
    state,
    pageCount,
  }: TableInstance = useTable(
    {
      columns,
      data,
      autoResetPage: autoReset ?? true,
      defaultColumn,
      initialState: {
        pageIndex: pageIndex ? Number(pageIndex) - 1 : 0,
      },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const size = data.length;
  const defaultSize = 10;

  const paginationOptions = {
    ...state,
    setPageSize,
    gotoPage: (page: number) => {
      gotoPage(page);
      if (useQueryParams) {
        const params = new URLSearchParams();
        params.append('page', (page + 1).toString());
        history.push({ search: params.toString() });
      }
    },
    pageOptions,
    size,
    selector,
    pageCount,
    controlledPagination: false,
  };

  useEffect(() => {
    setPageSize(!!pagination ? defaultSize : 1000);
  }, [pagination, setPageSize]);

  return (
    <TableStyled>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, index: any) => (
                <th
                  {...column.getHeaderProps([{ className: column.className }])}
                >
                  {column.render('Header')}
                  {column.hasFilter && (
                    <div data-testid={column.testid}>
                      {column.render('Filter')}
                    </div>
                  )}
                  {column.SortedBy ? (
                    <div data-testid={column.testid}>
                      {column.render('SortedBy')}
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {!isLoading ? (
            page.map((row: Row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps(rowProps?.(row))}>
                  {row.cells.map((cell: any) => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })
          ) : (
            <Skeleton
              /* pass from parent component? */
              rows={3}
              columns={columns.length}
            />
          )}
          {!isLoading && size === 0 && (
            <tr>
              <td colSpan={100}>{noResultsMessage}</td>
            </tr>
          )}
        </tbody>
      </table>
      {pagination && <Pagination {...paginationOptions} size={data.length} />}
    </TableStyled>
  );
};
